/**
 * Local Storage abstraction layer for game state persistence
 */

const STORAGE_KEYS = {
    MISSION_STATE: 'liftProgrammer_missionState',
    LIBRARIES: 'liftProgrammer_libraries',
    USER_CODE: 'liftProgrammer_userCode',
} as const;

type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

/**
 * Safely get an item from local storage
 */
export function getItem<T>(key: StorageKey): T | null {
    try {
        const item = localStorage.getItem(key);
        if (item === null) {
            return null;
        }
        return JSON.parse(item) as T;
    } catch {
        return null;
    }
}

/**
 * Safely set an item in local storage
 */
export function setItem<T>(key: StorageKey, value: T): boolean {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
    } catch {
        return false;
    }
}

/**
 * Remove an item from local storage
 */
export function removeItem(key: StorageKey): boolean {
    try {
        localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

/**
 * Check if local storage is available
 */
export function isAvailable(): boolean {
    try {
        const testKey = '__storage_test__';
        localStorage.setItem(testKey, testKey);
        localStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
}

// Export storage keys for use in other modules
export { STORAGE_KEYS };
